import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setUnregisteredStudents,
  setRegisteredStudents,
  setStudentsLoading,
  setStudentsError,
  setLastRegInfo,
  setActiveTab,
  setCalculation,
  setCalcLoading,
  setAccommodationConfig,
  setSavedVisitDates as persistVisitDates,
  setPaymentInfo,
  setPendingEditPayload,
} from '../../../redux/slices/ykVisitSlice';
import ykVisitService from '../services/ykVisitService';
import { DEFAULT_VISIT_DATE_ROW, calculateNights, calculateAccommodationAmount } from '../models/ykVisitModels';
import './YkVisitRegistration.css';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const getAvatarImage = (gender) => {
  const g = (gender || '').toLowerCase().trim();
  const isMale = g === 'male' || g === 'm';
  const isFemale = g === 'female' || g === 'f';
  const grad = isMale
    ? '<stop offset="0%" style="stop-color:#60a5fa"/><stop offset="100%" style="stop-color:#3b82f6"/>'
    : isFemale
    ? '<stop offset="0%" style="stop-color:#f9a8d4"/><stop offset="100%" style="stop-color:#ec4899"/>'
    : '<stop offset="0%" style="stop-color:#cbd5e1"/><stop offset="100%" style="stop-color:#94a3b8"/>';
  const emoji = isMale ? '\ud83d\udc66' : isFemale ? '\ud83d\udc67' : '\ud83d\udc64';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">${grad}</linearGradient></defs><circle cx="100" cy="100" r="95" fill="url(#g)"/><text x="100" y="145" font-size="140" text-anchor="middle" font-family="Arial">${emoji}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// ─── sub-components ─────────────────────────────────────────────────────────

const LoadingSpinner = ({ label = 'Loading...' }) => (
  <div className="ykreg-loading">
    <div className="inline-block ykreg-spinner"></div>
    <p className="ykreg-loading-text">{label}</p>
  </div>
);

const ErrorBanner = ({ msg }) => (
  <div className="ykreg-error-banner">
    <p className="ykreg-error-text">{msg}</p>
  </div>
);

// ─── Visit Date Form ─────────────────────────────────────────────────────────

const VisitDateForm = ({ rows, onRowChange, onAddRow, onRemoveRow, onSave, saving, saveError, accommodationConfig }) => {
  const [rowErrors, setRowErrors] = useState([]);
  const pricePerNight = parseFloat(accommodationConfig?.accommodationFee || 0);
  const transportFee = parseFloat(accommodationConfig?.transportFee || 0);

  const validate = () => {
    const errs = rows.map((r) => {
      const e = {};
      if (!r.start_Date) e.start_Date = 'Start date is required';
      if (!r.end_Date) e.end_Date = 'End date is required';
      else if (r.start_Date && r.end_Date < r.start_Date) e.end_Date = 'End date must be on or after start date';
      return e;
    });
    setRowErrors(errs);
    return errs.every((e) => Object.keys(e).length === 0);
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave();
  };

  const handleFieldChange = (idx, field, value) => {
    onRowChange(idx, field, value);
    // Auto-select/deselect accommodation based on nights when end date changes
    if (field === 'end_Date' && value) {
      const nights = calculateNights(rows[idx].start_Date, value);
      onRowChange(idx, 'is_Accommodation_Needed', nights >= 1 ? 'Y' : 'N');
    }
    if (rowErrors[idx]?.[field]) {
      setRowErrors((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx] };
        delete next[idx][field];
        return next;
      });
    }
  };

  return (
    <div className="ykreg-date-form">
      <h4 className="ykreg-date-form-title">Visit Date Rows</h4>

      {saveError && <ErrorBanner msg={saveError} />}

      {rows.map((row, idx) => {
        const errs = rowErrors[idx] || {};
        return (
          <div key={idx} className="ykreg-date-row">
            <div className="ykreg-date-row-header">
              <span className="ykreg-date-row-label">Visit Period {idx + 1}</span>
              {rows.length > 1 && (
                <button type="button" onClick={() => onRemoveRow(idx)}
                  className="ykreg-remove-row-btn">
                  ✕ Remove
                </button>
              )}
            </div>

            <div className="ykreg-date-grid">
              <div>
                <label className="ykreg-field-label">Start Date *</label>
                <input type="date" value={row.start_Date} min={todayStr()}
                  onChange={(e) => handleFieldChange(idx, 'start_Date', e.target.value)}
                  className={`ykreg-date-input${errs.start_Date ? ' ykreg-input-error' : ''}`} />
                {errs.start_Date && <p className="ykreg-field-error">{errs.start_Date}</p>}
              </div>
              <div>
                <label className="ykreg-field-label">End Date *</label>
                <input type="date" value={row.end_Date} min={row.start_Date || todayStr()}
                  onChange={(e) => handleFieldChange(idx, 'end_Date', e.target.value)}
                  className={`ykreg-date-input${errs.end_Date ? ' ykreg-input-error' : ''}`} />
                {errs.end_Date && <p className="ykreg-field-error">{errs.end_Date}</p>}
              </div>
            </div>

            <div className="ykreg-checkbox-group">
              <div>
                <label className="ykreg-checkbox-label">
                  <input type="checkbox" checked={row.is_Accommodation_Needed === 'Y'}
                    onChange={(e) => onRowChange(idx, 'is_Accommodation_Needed', e.target.checked ? 'Y' : 'N')}
                    className="ykreg-checkbox" />
                  <span className="ykreg-checkbox-text">Accommodation needed</span>
                </label>
                {row.is_Accommodation_Needed === 'Y' && (() => {
                  const nights = calculateNights(row.start_Date, row.end_Date);
                  const amt = calculateAccommodationAmount('Y', nights, pricePerNight);
                  return (
                    <div className="ykreg-accommodation-preview">
                      {nights > 0 ? (
                        <>
                          <span className="ykreg-accommodation-calc">
                            ${pricePerNight}/night × {nights} night{nights !== 1 ? 's' : ''} = <strong>${amt.toFixed(2)}</strong>
                          </span>
                          <span className="ykreg-accommodation-food">🍽 Food included</span>
                        </>
                      ) : (
                        <span className="ykreg-accommodation-calc">Select dates to see pricing</span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="ykreg-checkbox-label">
                  <input type="checkbox" checked={row.is_Transport === 'Y'}
                    onChange={(e) => onRowChange(idx, 'is_Transport', e.target.checked ? 'Y' : 'N')}
                    className="ykreg-checkbox" />
                  <span className="ykreg-checkbox-text">Transport needed</span>
                </label>
                {row.is_Transport === 'Y' && (
                  <div className="ykreg-accommodation-preview">
                    <span className="ykreg-accommodation-calc">
                      ✈ One-time airport → YK Visit: <strong>${transportFee.toFixed(2)}</strong>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <div className="ykreg-form-actions">
        <button type="button" onClick={onAddRow} className="ykreg-add-period-btn">
          + Add Another Period
        </button>
        <button type="button" onClick={handleSave} disabled={saving}
          className="ykreg-save-dates-btn">
          {saving ? 'Saving...' : 'Save Visit Dates'}
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const YkVisitRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campId = searchParams.get('campId');
  const centerId = searchParams.get('centerId');

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const userId = user?.User_Id || user?.user_Id || user?.userId;
  const loadingStudentsRef = useRef(false);

  const {
    unregisteredStudents: unregistered,
    registeredStudents: registered,
    studentsLoading,
    studentsError,
    activeTab,
    lastRegInfo,
    calculation,
    calcLoading,
    selectedCamp,
    accommodationConfig,
    savedVisitDates: persistedVisitDates,
  } = useSelector((state) => state.ykVisit);

  // Mobile fee summary modal
  const [showMobileSummary, setShowMobileSummary] = useState(false);

  // Visit date panel per student
  const [openStudentId, setOpenStudentId] = useState(null);
  const [visitRows, setVisitRows] = useState([{ ...DEFAULT_VISIT_DATE_ROW }]);
  const [savingVisit, setSavingVisit] = useState(false);
  const [visitSaveError, setVisitSaveError] = useState(null);

  // Registered student detail expansion
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [visitRecords, setVisitRecords] = useState({});  // { [studentId]: [] }
  const [loadingRecords, setLoadingRecords] = useState({});

  // Edit mode
  const [editingRecord, setEditingRecord] = useState(null);  // { studentId, record }
  const [editForm, setEditForm] = useState({ ...DEFAULT_VISIT_DATE_ROW });
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete
  const [deletingId, setDeletingId] = useState(null);

  // Add more visit dates for already-registered student
  const [addMoreStudentId, setAddMoreStudentId] = useState(null);
  const [addMoreRows, setAddMoreRows] = useState([{ ...DEFAULT_VISIT_DATE_ROW }]);
  const [savingMore, setSavingMore] = useState(false);
  const [addMoreError, setAddMoreError] = useState(null);

  // Saved visit dates — initialized from Redux so they survive navigation
  const [savedVisitDates, setSavedVisitDates] = useState(() => persistedVisitDates || []);

  // Add / Edit visitor modal
  const [showVisitorModal, setShowVisitorModal] = useState(false);
  const [visitorModalMode, setVisitorModalMode] = useState('add'); // 'add' | 'edit'
  const [visitorFormData, setVisitorFormData] = useState({ firstName: '', lastName: '', dateOfBirth: '', gender: '', relationship: '' });
  const [visitorFormErrors, setVisitorFormErrors] = useState({});
  const [selectedVisitorForEdit, setSelectedVisitorForEdit] = useState(null);
  const [savingVisitor, setSavingVisitor] = useState(false);
  const [visitorSaveError, setVisitorSaveError] = useState(null);

  // Payment
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [lookingUpBank, setLookingUpBank] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim(),
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    bankName: '',
  });

  // ── Load students + accommodation config on mount ───────────────────────
  useEffect(() => {
    if (userId && campId) {
      loadStudents();
      if (!accommodationConfig) {
        ykVisitService.getAccommodationFeeAndCampDate(campId)
          .then((raw) => {
            if (raw) {
              const fee = raw.accommodationFee ?? raw.accommodation_Fee ?? raw.Accommodation_Fee ?? raw.AccommodationFee ?? 0;
              dispatch(setAccommodationConfig({
                accommodationFee: parseFloat(fee) || 0,
                campStartDate: raw.campStartDate ?? raw.camp_Start_Date ?? raw.CampStartDate ?? null,
                campEndDate: raw.campEndDate ?? raw.camp_End_Date ?? raw.CampEndDate ?? null,
                transportFee: parseFloat(raw.transportFee ?? raw.transport_Fee ?? raw.Transport_Fee ?? 0) || 0,
              }));
            }
          })
          .catch(() => {});
      }
    }
  }, [userId, campId]);

  const loadStudents = async () => {
    if (loadingStudentsRef.current) return;
    loadingStudentsRef.current = true;
    dispatch(setStudentsLoading(true));
    dispatch(setStudentsError(null));
    try {
      const [unreg, reg] = await Promise.all([
        ykVisitService.getUnregisteredStudents(userId, campId),
        ykVisitService.getRegisteredStudents(userId, campId),
      ]);
      dispatch(setUnregisteredStudents(unreg || []));
      dispatch(setRegisteredStudents(reg || []));
    } catch (err) {
      dispatch(setStudentsError('Failed to load students. Please try again.'));
      console.error(err);
    } finally {
      dispatch(setStudentsLoading(false));
      loadingStudentsRef.current = false;
    }
  };

  // ── reCAPTCHA ──────────────────────────────────────────────────────────
  useEffect(() => {
    const ensureScript = () =>
      new Promise((resolve) => {
        if (window.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) { existing.addEventListener('load', () => resolve()); return; }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true; s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });

    const renderWidget = () => {
      const el = document.getElementById('ykvisit-recaptcha');
      if (!el || recaptchaWidgetId !== null) return;
      const poll = () => {
        const api = window.grecaptcha;
        if (!api) { setTimeout(poll, 150); return; }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') { setTimeout(doRender, 150); return; }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) { setTimeout(doRender, 200); }
        };
        if (typeof api.ready === 'function') { api.ready(doRender); } else { setTimeout(doRender, 0); }
      };
      poll();
    };

    let mounted = true;
    ensureScript().then(() => { if (mounted) setTimeout(renderWidget, 0); });
    return () => { mounted = false; };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);

  // ── auto-close success modal ────────────────────────────────────────────
  useEffect(() => {
    if (showSuccessModal) {
      const t = setTimeout(() => {
        setShowSuccessModal(false);
        navigate('/yk-visit/center');
      }, 20000);
      return () => clearTimeout(t);
    }
  }, [showSuccessModal, navigate]);

  // ── Visit date handlers ─────────────────────────────────────────────────
  const handleOpenVisitPanel = (studentId) => {
    if (openStudentId === studentId) {
      setOpenStudentId(null);
    } else {
      setOpenStudentId(studentId);
      // Pre-populate with saved dates when re-editing a pending student's visit
      const isReplacingPending = lastRegInfo && lastRegInfo.studentId === studentId;
      if (isReplacingPending && savedVisitDates.length > 0) {
        setVisitRows(savedVisitDates.map((r) => ({
          start_Date: r.start_Date ? r.start_Date.slice(0, 10) : '',
          end_Date: r.end_Date ? r.end_Date.slice(0, 10) : '',
          is_Accommodation_Needed: r.is_Accommodation_Needed || 'N',
          is_Transport: r.is_Transport || 'N',
        })));
      } else {
        setVisitRows([{ ...DEFAULT_VISIT_DATE_ROW }]);
      }
      setVisitSaveError(null);
    }
  };

  const handleRowChange = (idx, field, value) => {
    setVisitRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const handleAddRow = () => {
    setVisitRows((prev) => [...prev, { ...DEFAULT_VISIT_DATE_ROW }]);
  };

  const handleRemoveRow = (idx) => {
    setVisitRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveVisitDates = async (studentId) => {
    setSavingVisit(true);
    setVisitSaveError(null);
    try {
      // 0. If re-saving for an already-pending student, delete the old registration first
      const isReplacingPending = lastRegInfo && lastRegInfo.studentId === studentId;
      if (isReplacingPending) {
        try {
          await ykVisitService.deleteVisitRecord(campId, studentId, lastRegInfo.registrationId);
        } catch (delErr) {
          console.warn('Could not delete old pending registration:', delErr);
        }
        dispatch(setLastRegInfo(null));
        dispatch(setCalculation(null));
        dispatch(persistVisitDates([]));
        setSavedVisitDates([]);
      }

      // 1. Create a new Retreat_Registration header
      const regResult = await ykVisitService.addRegistration(userId, campId, studentId);
      const registrationId =
        regResult?.Retreat_Registration_Id ||
        regResult?.retreat_Registration_Id ||
        regResult?.id ||
        (typeof regResult === 'number' && regResult > 0 ? regResult : null);
      if (!registrationId) throw new Error('Invalid registration ID returned from server');

      // 2. Add each visit row as a detail (with calculated nights + accommodation fee)
      const pricePerNight = parseFloat(accommodationConfig?.accommodationFee || 0);
      for (const row of visitRows) {
        const nights = calculateNights(row.start_Date, row.end_Date);
        const accomFees = calculateAccommodationAmount(row.is_Accommodation_Needed, nights, pricePerNight);
        await ykVisitService.addRegistrationDetail(registrationId, campId, studentId, {
          ...row,
          no_Of_Days: nights,
          accommodation_Fees: accomFees,
        });
      }

      // 3. Store regInfo for calculation
      dispatch(setLastRegInfo({ studentId, registrationId }));

      // 4. Fetch calculation so the fee summary + Confirm button appear
      fetchCalculation(campId, studentId, registrationId);

      // 5. Close the date modal — user stays on Unregistered tab to confirm/pay
      setSavedVisitDates([...visitRows]);
      dispatch(persistVisitDates([...visitRows]));
      setOpenStudentId(null);
    } catch (err) {
      setVisitSaveError('Failed to save visit dates. Please try again.');
      console.error(err);
    } finally {
      setSavingVisit(false);
    }
  };

  const handleAddMoreVisitDates = async (studentId) => {
    setSavingMore(true);
    setAddMoreError(null);
    try {
      const recs = visitRecords[studentId] || [];
      let registrationId = recs[0]?.retreat_Registration_Id || recs[0]?.id;
      if (!registrationId) {
        const regResult = await ykVisitService.addRegistration(userId, campId, studentId);
        registrationId = regResult?.Retreat_Registration_Id || regResult?.retreat_Registration_Id ||
          regResult?.id || (typeof regResult === 'number' && regResult > 0 ? regResult : null);
        if (!registrationId) throw new Error('Invalid registration ID');
      }
      const pricePerNightMore = parseFloat(accommodationConfig?.accommodationFee || 0);
      for (const row of addMoreRows) {
        const nights = calculateNights(row.start_Date, row.end_Date);
        const accomFees = calculateAccommodationAmount(row.is_Accommodation_Needed, nights, pricePerNightMore);
        await ykVisitService.addRegistrationDetail(registrationId, campId, studentId, {
          ...row,
          no_Of_Days: nights,
          accommodation_Fees: accomFees,
        });
      }
      // Reload records immediately, then close modal
      const records = await ykVisitService.getVisitRecordsForStudent(campId, studentId);
      setVisitRecords((prev) => ({ ...prev, [studentId]: records || [] }));
      setAddMoreStudentId(null);
      // Fire-and-forget email
      const st = registered.find((s) => (s.student_Id || s.studentId) === studentId);
      const _toEmail = user?.User_Email || user?.user_Email || user?.email || '';
      const _userName = `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim();
      const _userPhone = user?.User_Phone || user?.user_Phone || '';
      const _vdr = addMoreRows[0]?.start_Date ? `${fmtDate(addMoreRows[0].start_Date)} \u2013 ${fmtDate(addMoreRows[addMoreRows.length - 1].end_Date)}` : 'N/A';
      ykVisitService.sendVisitConfirmationEmail({
        toEmail: _toEmail, userName: _userName, userPhone: _userPhone,
        campName: `Camp #${campId}`, visitDateRange: _vdr,
        students: st ? [st] : [], registrationId,
        transactionDate: new Date().toLocaleString(),
      }).catch((e) => console.warn('Add-more email:', e));
    } catch (err) {
      setAddMoreError('Failed to add visit dates. Please try again.');
      console.error(err);
    } finally {
      setSavingMore(false);
    }
  };

  // ── Calculation ─────────────────────────────────────────────────────────
  const fetchCalculation = async (cId, sId, rId) => {
    if (!cId || !sId || !rId) return;
    dispatch(setCalcLoading(true));
    try {
      const data = await ykVisitService.getCalculation(cId, sId, rId);
      dispatch(setCalculation(data));
    } catch (err) {
      console.error('Calculation error:', err);
      dispatch(setCalcLoading(false));
    }
  };

  // ── Expand registered student (load visit records) ──────────────────────
  const handleExpandStudent = async (studentId) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null);
      return;
    }
    setExpandedStudentId(studentId);
    if (!visitRecords[studentId]) {
      setLoadingRecords((prev) => ({ ...prev, [studentId]: true }));
      try {
        const records = await ykVisitService.getVisitRecordsForStudent(campId, studentId);
        setVisitRecords((prev) => ({ ...prev, [studentId]: records || [] }));
        if (records && records.length > 0) {
          const r = records[0];
          fetchCalculation(campId, studentId, r.retreat_Registration_Id);
          dispatch(setLastRegInfo({ studentId, registrationId: r.retreat_Registration_Id }));
        }
      } catch (err) {
        console.error('Error loading visit records:', err);
      } finally {
        setLoadingRecords((prev) => ({ ...prev, [studentId]: false }));
      }
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async (studentId, detailId) => {
    if (!window.confirm('Delete this visit record? This cannot be undone.')) return;
    setDeletingId(detailId);
    try {
      await ykVisitService.deleteVisitRecord(campId, studentId, detailId);
      setVisitRecords((prev) => ({
        ...prev,
        [studentId]: (prev[studentId] || []).filter(
          (r) => (r.retreat_Registration_Details_Id || r.id) !== detailId
        ),
      }));
      await loadStudents();
    } catch (err) {
      alert('Failed to delete visit record. Please try again.');
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────────
  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();

  const handleStartEdit = (studentId, record) => {
    setEditingRecord({ studentId, record });
    setEditForm({
      start_Date: record.start_Date ? record.start_Date.slice(0, 10) : '',
      end_Date: record.end_Date ? record.end_Date.slice(0, 10) : '',
      is_Accommodation_Needed: record.is_Accommodation_Needed || 'N',
      is_Transport: record.is_Transport || 'N',
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.start_Date || !editForm.end_Date) { alert('Start and end dates are required.'); return; }
    if (editForm.end_Date < editForm.start_Date) { alert('End date must be on or after start date.'); return; }
    const nights = calculateNights(editForm.start_Date, editForm.end_Date);
    const fee = calculateAccommodationAmount(editForm.is_Accommodation_Needed, nights, parseFloat(accommodationConfig?.accommodationFee || 0));
    const editPayload = {
      retreat_Registration_Details_Id: editingRecord.record.retreat_Registration_Details_Id || editingRecord.record.id,
      retreat_Registration_Id: editingRecord.record.retreat_Registration_Id,
      student_Id: editingRecord.studentId,
      camp_Id: campId,
      start_Date: editForm.start_Date,
      end_Date: editForm.end_Date,
      is_Accommodation_Needed: editForm.is_Accommodation_Needed,
      is_Transport: editForm.is_Transport,
      registration_Fee: fee,
      accommodation_Fees: fee,
      no_Of_Days: nights,
    };
    if (fee > 0) {
      const student = registered.find((s) => (s.student_Id || s.studentId) === editingRecord.studentId) ||
                      unregistered.find((s) => (s.student_Id || s.studentId) === editingRecord.studentId);
      dispatch(setPendingEditPayload(editPayload));
      dispatch(setPaymentInfo({
        campId,
        campName: selectedCamp?.Camp_Name || selectedCamp?.camp_Name || `Camp #${campId}`,
        userId,
        userEmail: user?.User_Email || user?.user_Email || '',
        userFirstName: user?.User_First_Name || user?.user_First_Name || '',
        userLastName: user?.User_Last_Name || user?.user_Last_Name || '',
        userPhone: user?.User_Phone || user?.user_Phone || '',
        studentName: student ? `${student.student_First_Name || ''} ${student.student_Last_Name || ''}`.trim() : 'Visitor',
        studentGender: student?.student_Gender || student?.gender || '',
        registrationId: editingRecord.record.retreat_Registration_Id,
        totalAmount: fee,
        regFee: 0,
        accommodationAmt: fee,
        nights,
        pricePerNight: parseFloat(accommodationConfig?.accommodationFee || 0),
        visitDates: [{ start_Date: editForm.start_Date, end_Date: editForm.end_Date }],
        centerId,
        streetAddress1: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
        streetAddress2: user?.User_Address_Line2 || user?.user_Address_Line2 || '',
        city: user?.User_City || user?.user_City || '',
        zipCode: user?.User_Zipcode || user?.user_Zipcode || '',
        country: 'US',
        entity: 'RMS',
      }));
      navigate('/yk-visit/payment');
    } else {
      setSavingEdit(true);
      try {
        await ykVisitService.updateVisitDetail(editPayload);
        const records = await ykVisitService.getVisitRecordsForStudent(campId, editingRecord.studentId);
        setVisitRecords((prev) => ({ ...prev, [editingRecord.studentId]: records || [] }));
        setEditingRecord(null);
      } catch (err) {
        const status = err?.response?.status;
        const msg = err?.response?.data?.message || err?.response?.data || err?.message || 'Unknown error';
        alert(`Failed to update visit record. (${status || 'Network error'}: ${typeof msg === 'string' ? msg : JSON.stringify(msg)})`);
      } finally {
        setSavingEdit(false);
      }
    }
  };

  // ── Payment handlers ────────────────────────────────────────────────────
  const handlePaymentInput = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'cardNumber') { const d = v.replace(/\D/g, ''); v = d.replace(/(\d{4})(?=\d)/g, '$1 ').trim(); }
    if (name === 'expiryDate') { const d = v.replace(/\D/g, ''); v = d.length <= 2 ? d : d.slice(0, 2) + '/' + d.slice(2, 4); }
    if (name === 'cvv') { v = v.replace(/\D/g, ''); }
    if (name === 'routingNumber') { v = v.replace(/\D/g, '').slice(0, 9); }
    if (name === 'accountNumber') { v = v.replace(/\D/g, ''); }
    setPaymentForm((prev) => ({ ...prev, [name]: v }));
    if (validationErrors[name]) { setValidationErrors((prev) => { const e = { ...prev }; delete e[name]; return e; }); }
    if (name === 'routingNumber' && v.length === 9) { lookupBank(v); }
  };

  const lookupBank = async (routing) => {
    setLookingUpBank(true);
    const name = await ykVisitService.getBankName(routing);
    setPaymentForm((prev) => ({ ...prev, bankName: name }));
    setLookingUpBank(false);
  };

  const validateCC = () => {
    const e = {};
    const digits = paymentForm.cardNumber.replace(/\D/g, '');
    if (!digits) e.cardNumber = 'Card number is required';
    else if (digits.length < 13 || digits.length > 19) e.cardNumber = 'Card number must be 13–19 digits';
    if (!paymentForm.expiryDate) { e.expiryDate = 'Expiry date is required'; }
    else {
      const m = paymentForm.expiryDate.match(/^(\d{2})\/(\d{2})$/);
      if (!m) { e.expiryDate = 'Use MM/YY format'; }
      else {
        const mo = parseInt(m[1], 10), yr = parseInt(m[2], 10);
        if (mo < 1 || mo > 12) e.expiryDate = 'Invalid month';
        else {
          const now = new Date(), cy = now.getFullYear() % 100, cm = now.getMonth() + 1;
          if (yr < cy || (yr === cy && mo < cm)) e.expiryDate = 'Card has expired';
        }
      }
    }
    if (!paymentForm.cvv) e.cvv = 'CVV is required';
    else if (paymentForm.cvv.length < 3) e.cvv = 'CVV must be 3–4 digits';
    return e;
  };

  const validateACH = () => {
    const e = {};
    if (!paymentForm.accountHolderName?.trim() || paymentForm.accountHolderName.trim().length < 2)
      e.accountHolderName = 'Account holder name is required';
    if (!paymentForm.routingNumber) e.routingNumber = 'Routing number is required';
    else if (paymentForm.routingNumber.length !== 9) e.routingNumber = 'Routing number must be exactly 9 digits';
    if (!paymentForm.accountNumber) e.accountNumber = 'Account number is required';
    else if (paymentForm.accountNumber.length < 5 || paymentForm.accountNumber.length > 17)
      e.accountNumber = 'Account number must be 5–17 digits';
    if (!paymentForm.bankName?.trim() || paymentForm.bankName.trim().length < 2) e.bankName = 'Bank name is required';
    return e;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!recaptchaToken) {
      setValidationErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
      document.getElementById('ykvisit-recaptcha')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const errors = paymentMethod === 'credit' ? validateCC() : validateACH();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setProcessing(true);
    try {
      const netToPay = calculation?.net_To_Pay ?? calculation?.netToPay ?? 0;
      const paymentDetails = {
        paymentType: paymentMethod === 'credit' ? 'CreditCard' : 'ACH',
        amount: netToPay,
        campId,
        campName: '',
        userId,
        userEmail: user?.User_Email || user?.user_Email || '',
        userFirstName: user?.User_First_Name || user?.user_First_Name || '',
        userLastName: user?.User_Last_Name || user?.user_Last_Name || '',
        userPhone: user?.User_Phone || user?.user_Phone || '',
        streetAddress1: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
        streetAddress2: user?.User_Address_Line2 || user?.user_Address_Line2 || '',
        city: user?.User_City || user?.user_City || '',
        state: 'GA',
        zipCode: user?.User_Zipcode || user?.user_Zipcode || '',
        country: 'US',
        entity: 'RMS',
        ...(paymentMethod === 'credit'
          ? { cardNumber: paymentForm.cardNumber.replace(/\s/g, ''), expiryDate: paymentForm.expiryDate, cvv: paymentForm.cvv }
          : { routingNumber: paymentForm.routingNumber, accountNumber: paymentForm.accountNumber, accountType: paymentForm.accountType, bankName: paymentForm.bankName }),
      };

      const resp = await ykVisitService.processPayment(paymentDetails);
      const approved =
        resp?.createTransactionResponse?.transactionResponse?.responseCode === '1' ||
        resp?.createTransactionResponse?.messages?.resultCode === 'Ok';

      if (resp && approved) {
        const transId = resp?.createTransactionResponse?.transactionResponse?.transId || '';
        // Update payment status
        if (lastRegInfo) {
          await ykVisitService.updatePaymentStatus({
            Camp_Id: parseInt(campId),
            User_Id: parseInt(userId),
            Camp_Payment_Id: lastRegInfo.registrationId,
            Camp_Order_Id: 0,
            Receipt_Id: transId || '0',
          });
        }
        // Reload students then clear pending state
        await loadStudents();
        dispatch(setLastRegInfo(null));
        dispatch(setCalculation(null));
        dispatch(persistVisitDates([]));
        setSavedVisitDates([]);
        // Send confirmation email (fire-and-forget)
        const _toEmail = user?.User_Email || user?.user_Email || user?.email || '';
        const _userName = `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim();
        const _userPhone = user?.User_Phone || user?.user_Phone || '';
        const _campName = selectedCamp?.Camp_Name || selectedCamp?.camp_Name || selectedCamp?.campName || `Camp #${campId}`;
        const _visitDates = savedVisitDates.length > 0
          ? savedVisitDates.map(r => `${fmtDate(r.start_Date)} – ${fmtDate(r.end_Date)}`).join(', ')
          : 'Visit Dates Registered';
        ykVisitService.sendVisitConfirmationEmail({
          toEmail: _toEmail,
          userName: _userName || 'Visitor',
          userPhone: _userPhone || 'N/A',
          campName: _campName,
          visitDateRange: _visitDates,
          students: [],
          registrationId: lastRegInfo?.registrationId || 0,
          transactionDate: new Date().toLocaleString(),
          amount: String(netToPay ?? 0),
          paymentType: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          transId,
        }).catch(e => console.warn('Payment confirm email:', e));
        setPaymentResult({ transId, amount: netToPay });
        setShowSuccessModal(true);
      } else {
        const msg = resp?.createTransactionResponse?.transactionResponse?.errors?.[0]?.errorText ||
          resp?.createTransactionResponse?.messages?.message?.[0]?.text || 'Payment declined. Please try again.';
        setErrorMessage(msg);
        setShowErrorModal(true);
      }
    } catch (err) {
      setErrorMessage('Payment processing failed. Please try again.');
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleFreeConfirm = async () => {
    setProcessing(true);
    try {
      if (lastRegInfo) {
        await ykVisitService.updatePaymentStatus({
          Camp_Id: parseInt(campId),
          User_Id: parseInt(userId),
          Camp_Payment_Id: lastRegInfo.registrationId,
          Camp_Order_Id: 0,
          Receipt_Id: 'FREE',
        });
      }
      // Reload students then clear pending state
      await loadStudents();
      dispatch(setLastRegInfo(null));
      dispatch(setCalculation(null));
      dispatch(persistVisitDates([]));
      setSavedVisitDates([]);
      setPaymentResult({ transId: 'FREE', amount: 0 });
      setShowSuccessModal(true);
      // Send confirmation email (fire-and-forget)
      const _toEmail = user?.User_Email || user?.user_Email || user?.email || '';
      const _userName = `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim();
      const _userPhone = user?.User_Phone || user?.user_Phone || '';
      const _campName = selectedCamp?.Camp_Name || selectedCamp?.camp_Name || selectedCamp?.campName || `Camp #${campId}`;
      const _visitDates = savedVisitDates.length > 0
        ? savedVisitDates.map(r => `${fmtDate(r.start_Date)} – ${fmtDate(r.end_Date)}`).join(', ')
        : 'Visit Dates Registered';
      const _studentList = lastRegInfo?.studentId
        ? (registered.find(s => (s.student_Id || s.studentId) === lastRegInfo.studentId) ||
           unregistered.find(s => (s.student_Id || s.studentId) === lastRegInfo.studentId))
        : null;
      ykVisitService.sendVisitConfirmationEmail({
        toEmail: _toEmail,
        userName: _userName || 'Visitor',
        userPhone: _userPhone || 'N/A',
        campName: _campName,
        visitDateRange: _visitDates,
        students: _studentList ? [_studentList] : [],
        registrationId: lastRegInfo?.registrationId || 0,
        transactionDate: new Date().toLocaleString(),
        amount: '0',
        paymentType: 'Free',
        transId: 'FREE',
      }).catch(e => console.warn('Free confirm email:', e));
    } catch (err) {
      setErrorMessage('Failed to confirm registration. Please try again.');
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  // ── Visitor add / edit handlers ─────────────────────────────────────────
  const handleOpenAddVisitor = () => {
    setVisitorFormData({ firstName: '', lastName: '', dateOfBirth: '', gender: '', relationship: '' });
    setVisitorFormErrors({});
    setVisitorSaveError(null);
    setSelectedVisitorForEdit(null);
    setVisitorModalMode('add');
    setShowVisitorModal(true);
  };

  const handleOpenEditVisitor = (e, student) => {
    e.stopPropagation();
    let dob = student.student_Date_Of_Birth || student.dateOfBirth || '';
    if (dob) { try { dob = dob.includes('T') ? dob.split('T')[0] : dob; } catch (_) {} }
    let gender = student.student_Gender || student.gender || '';
    if (gender === 'M' || gender === 'm') gender = 'Male';
    else if (gender === 'F' || gender === 'f') gender = 'Female';
    setVisitorFormData({
      firstName: student.student_First_Name || student.firstName || '',
      lastName: student.student_Last_Name || student.lastName || '',
      dateOfBirth: dob,
      gender,
      relationship: student.student_Relationship_To_User || student.relationship || student.student_Relationship || '',
    });
    setVisitorFormErrors({});
    setVisitorSaveError(null);
    setSelectedVisitorForEdit(student);
    setVisitorModalMode('edit');
    setShowVisitorModal(true);
  };

  const handleCloseVisitorModal = () => {
    setShowVisitorModal(false);
    setSelectedVisitorForEdit(null);
    setVisitorFormData({ firstName: '', lastName: '', dateOfBirth: '', gender: '', relationship: '' });
    setVisitorFormErrors({});
    setVisitorSaveError(null);
  };

  const handleSaveVisitor = async () => {
    const errors = {};
    if (!visitorFormData.firstName.trim()) errors.firstName = true;
    if (!visitorFormData.lastName.trim()) errors.lastName = true;
    if (!visitorFormData.gender) errors.gender = true;
    if (!visitorFormData.relationship) errors.relationship = true;
    if (!visitorFormData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    } else {
      const dob = new Date(visitorFormData.dateOfBirth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dob > today) {
        errors.dateOfBirth = 'Date of birth cannot be in the future';
      } else {
        const ageMs = today - dob;
        const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
        if (ageYears < 3) errors.dateOfBirth = 'Visitor must be at least 3 years old';
      }
    }
    setVisitorFormErrors(errors);
    if (Object.values(errors).some(Boolean)) return;
    setSavingVisitor(true);
    setVisitorSaveError(null);
    try {
      if (visitorModalMode === 'edit' && selectedVisitorForEdit) {
        const sId = selectedVisitorForEdit.student_Id || selectedVisitorForEdit.studentId;
        await ykVisitService.updateVisitor(userId, sId, visitorFormData);
      } else {
        await ykVisitService.addVisitor(userId, visitorFormData);
      }
      await loadStudents();
      handleCloseVisitorModal();
    } catch (err) {
      setVisitorSaveError(err?.response?.data?.error || err?.response?.data?.title || err.message || 'Failed to save. Please try again.');
    } finally {
      setSavingVisitor(false);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────
  const pricePerNightGlobal = parseFloat(accommodationConfig?.accommodationFee || 0);
  const transportFeeGlobal = parseFloat(accommodationConfig?.transportFee || 0);
  const serverAccomAmt = parseFloat(calculation?.accommodation_Fees ?? calculation?.accommodationFees ?? 0);
  const localAccomOverride = (() => {
    if (!calculation || !pricePerNightGlobal || !savedVisitDates?.length) return null;
    const hasAccom = savedVisitDates.some((r) => r.is_Accommodation_Needed === 'Y');
    if (!hasAccom) return null;
    const nights = savedVisitDates.reduce((s, r) => s + calculateNights(r.start_Date, r.end_Date), 0);
    return nights * pricePerNightGlobal;
  })();
  const effectiveAccomAmt = localAccomOverride !== null ? Math.max(localAccomOverride, serverAccomAmt) : serverAccomAmt;
  const serverRegFee = parseFloat(calculation?.registration_Fee ?? calculation?.registrationFee ?? 0);
  const serverAmountPaid = parseFloat(calculation?.amount_Paid ?? calculation?.amountPaid ?? 0);
  const hasTransport = savedVisitDates.some((r) => r.is_Transport === 'Y');
  const transportTotal = hasTransport ? transportFeeGlobal : 0;
  const netToPay = calculation !== null
    ? Math.max(serverRegFee + effectiveAccomAmt + transportTotal - serverAmountPaid, 0)
    : null;
  const isFree = netToPay !== null && netToPay <= 0;

  const FieldError = ({ field }) =>
    validationErrors[field] ? (
      <p className="ykreg-field-error">{validationErrors[field]}</p>
    ) : null;

  const renderVisitorForm = () => (
    <div className="text-left">
      <div className="mb-2">
        <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
        <input type="text" value={visitorFormData.firstName}
          onChange={e => setVisitorFormData(p => ({ ...p, firstName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))}
          placeholder="First name"
          className={`w-full border rounded-lg px-2.5 py-1.5 text-xs outline-none ${visitorFormErrors.firstName ? 'border-red-400 bg-red-50' : 'border-gray-300 focus:border-indigo-400'}`} />
        {visitorFormErrors.firstName && <p className="text-red-500 text-xs mt-0.5">Required</p>}
      </div>
      <div className="mb-2">
        <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name *</label>
        <input type="text" value={visitorFormData.lastName}
          onChange={e => setVisitorFormData(p => ({ ...p, lastName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))}
          placeholder="Last name"
          className={`w-full border rounded-lg px-2.5 py-1.5 text-xs outline-none ${visitorFormErrors.lastName ? 'border-red-400 bg-red-50' : 'border-gray-300 focus:border-indigo-400'}`} />
        {visitorFormErrors.lastName && <p className="text-red-500 text-xs mt-0.5">Required</p>}
      </div>
      <div className="mb-2">
        <label className="block text-xs font-semibold text-gray-600 mb-1">Date of Birth *</label>
        <input type="date" value={visitorFormData.dateOfBirth}
          onChange={e => setVisitorFormData(p => ({ ...p, dateOfBirth: e.target.value }))}
          className={`w-full border rounded-lg px-2.5 py-1.5 text-xs outline-none ${visitorFormErrors.dateOfBirth ? 'border-red-400 bg-red-50' : 'border-gray-300 focus:border-indigo-400'}`} />
        {visitorFormErrors.dateOfBirth && <p className="text-red-500 text-xs mt-0.5">Required</p>}
      </div>
      <div className="mb-2">
        <label className="block text-xs font-semibold text-gray-600 mb-1">Gender *</label>
        <select value={visitorFormData.gender}
          onChange={e => setVisitorFormData(p => ({ ...p, gender: e.target.value }))}
          className={`w-full border rounded-lg px-2.5 py-1.5 text-xs outline-none bg-white ${visitorFormErrors.gender ? 'border-red-400 bg-red-50' : 'border-gray-300 focus:border-indigo-400'}`}>
          <option value="">Select gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
        {visitorFormErrors.gender && <p className="text-red-500 text-xs mt-0.5">Required</p>}
      </div>
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-600 mb-1">Relationship *</label>
        <select value={visitorFormData.relationship}
          onChange={e => setVisitorFormData(p => ({ ...p, relationship: e.target.value }))}
          className={`w-full border rounded-lg px-2.5 py-1.5 text-xs outline-none bg-white ${visitorFormErrors.relationship ? 'border-red-400 bg-red-50' : 'border-gray-300 focus:border-indigo-400'}`}>
          <option value="">Select</option>
          <option value="Self">Self</option>
          <option value="Spouse">Spouse</option>
          <option value="Son">Son</option>
          <option value="Daughter">Daughter</option>
          <option value="Other">Other</option>
        </select>
        {visitorFormErrors.relationship && <p className="text-red-500 text-xs mt-0.5">Required</p>}
      </div>
      {visitorSaveError && <p className="text-red-500 text-xs mb-2">{visitorSaveError}</p>}
      <div className="flex gap-2">
        <button onClick={handleCloseVisitorModal}
          className="flex-1 text-xs py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 font-semibold cursor-pointer hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={handleSaveVisitor} disabled={savingVisitor}
          className="flex-1 text-xs py-1.5 rounded-lg border-none text-white font-semibold cursor-pointer"
          style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', opacity: savingVisitor ? 0.7 : 1 }}>
          {savingVisitor ? 'Saving...' : selectedVisitorForEdit ? 'Update' : 'Add Visitor'}
        </button>
      </div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="ykreg-page">
      <div className="container-custom">
        <div className="ykreg-header">
          <button onClick={() => navigate(`/yk-visit/selection?centerId=${centerId}`)}
            className="ykreg-back-btn">
            ← Back to Visit Selection
          </button>
          <h1 className="ykreg-title">YK Visit Registration</h1>
        </div>

        {studentsLoading && <LoadingSpinner label="Loading students..." />}
        {studentsError && <ErrorBanner msg={studentsError} />}

        {!studentsLoading && (
          <div className="ykreg-layout">
            <div className="lg:col-span-2">
              {/* ── Unified Visitor Grid (no tabs) ── */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-base font-semibold text-gray-700">
                  All Visitors
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    · <span className="text-green-600 font-medium">{registered.length} registered</span>
                    {unregistered.length > 0 && <span className="text-gray-400"> · {unregistered.length} pending</span>}
                  </span>
                </h3>
                <button
                  onClick={handleOpenAddVisitor}
                  className="text-xs font-semibold py-1.5 px-4 rounded-full border-none text-white cursor-pointer transition"
                  style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}
                >+ Add Visitor</button>
              </div>
              {(unregistered.length === 0 && registered.length === 0) && (
                <div className="ykreg-empty">
                  <div className="ykreg-empty-icon">👥</div>
                  <p className="ykreg-empty-text">No visitors found for this camp.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" style={{gridAutoRows: 'minmax(220px, auto)'}}>
                {/* ── Unregistered / pending visitors ── */}
                {unregistered.map((student) => {
                  const sId = student.student_Id || student.studentId;
                  const isOpen = openStudentId === sId;
                  const isPending = lastRegInfo && lastRegInfo.studentId === sId;
                  const gender = (student.student_Gender || student.gender || '').toLowerCase();
                  const firstName = student.student_First_Name || student.firstName || '';
                  const lastName = student.student_Last_Name || student.lastName || '';
                  const relationship = student.relationship || student.student_Relationship || '';
                  const dob = student.student_Date_Of_Birth || student.dateOfBirth;
                  return (
                    <div key={`unreg-${sId}`}
                      className={`relative rounded-3xl p-5 shadow-md border-2 text-center transition-all duration-200 bg-white hover:shadow-lg flex flex-col ${
                        isOpen ? 'border-indigo-400 ring-2 ring-indigo-200'
                        : isPending ? 'border-yellow-400 ring-2 ring-yellow-100'
                        : 'border-gray-200 hover:border-indigo-300'
                      }`}>
                      {isPending && (
                        <div className="absolute top-2 right-2">
                          <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">⏳ Pending</span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex justify-center mb-3 mt-1">
                          <img src={getAvatarImage(gender)} alt={firstName} className="w-16 h-16 rounded-full object-cover" />
                        </div>
                        <p className="font-bold text-gray-800 text-sm leading-tight">{firstName} {lastName}</p>
                        {relationship && <p className="text-xs text-gray-500 mt-0.5 capitalize">{relationship}</p>}
                        {dob && <p className="text-xs text-gray-400 mt-0.5">{fmtDate(dob)}</p>}
                      </div>
                      <button onClick={() => handleOpenVisitPanel(sId)}
                        className={`mt-3 w-full text-xs font-semibold py-2 px-3 rounded-full cursor-pointer transition ykreg-add-visit-btn${isOpen ? ' open' : ''}`}
                      >{isOpen ? '✕ Cancel' : (isPending ? '✏ Edit Visit Dates' : '+ Add Visit Dates')}</button>
                      {isPending && !isOpen && (
                        <p className="mt-1 text-xs text-yellow-600 font-medium">Awaiting confirmation below ↓</p>
                      )}
                      <button onClick={(e) => handleOpenEditVisitor(e, student)}
                        className="mt-1 w-full text-xs font-semibold py-1.5 px-3 rounded-full border border-gray-300 text-gray-600 bg-gray-50 hover:bg-gray-100 cursor-pointer transition"
                      >✏️ Edit Visitor</button>
                    </div>
                  );
                })}
                {/* ── Registered visitors ── */}
                {registered.map((student) => {
                  const sId = student.student_Id || student.studentId;
                  const gender = (student.student_Gender || student.gender || '').toLowerCase();
                  const firstName = student.student_First_Name || student.firstName || '';
                  const lastName = student.student_Last_Name || student.lastName || '';
                  const relationship = student.relationship || student.student_Relationship || '';
                  return (
                    <div key={`reg-${sId}`}
                      className="relative rounded-3xl p-5 shadow-md border-2 text-center transition-all duration-200 bg-white border-gray-200 hover:border-green-300 hover:shadow-lg flex flex-col">
                      <div className="absolute top-2 right-2">
                        <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">✓ Registered</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-center mb-3 mt-2">
                          <img src={getAvatarImage(gender)} alt={firstName} className="w-16 h-16 rounded-full object-cover" />
                        </div>
                        <p className="font-bold text-gray-800 text-sm leading-tight">{firstName} {lastName}</p>
                        {relationship && <p className="text-xs text-gray-500 mt-0.5 capitalize">{relationship}</p>}
                      </div>
                      <div className="mt-3 flex flex-col gap-1.5">
                        <button onClick={() => handleExpandStudent(sId)}
                          className="w-full text-xs font-semibold py-1.5 px-3 rounded-full border border-green-400 text-green-700 bg-green-50 hover:bg-green-100 cursor-pointer transition"
                        >📋 Visit Records</button>
                        <button onClick={(e) => handleOpenEditVisitor(e, student)}
                          className="w-full text-xs font-semibold py-1.5 px-3 rounded-full border border-gray-300 text-gray-600 bg-gray-50 hover:bg-gray-100 cursor-pointer transition"
                        >✏️ Edit Visitor</button>
                      </div>
                    </div>
                  );
                })}
              </div>


            </div>


            <div className="ykreg-sidebar hidden lg:block">

              {/* ── Fee Calculation ── */}
              {calcLoading && (
                <div className="ykreg-calc-loading">
                  <div className="ykreg-spinner-sm"></div>
                  <p className="ykreg-calc-loading-text">Calculating fees...</p>
                </div>
              )}

              {!calcLoading && calculation && (
                <div className="ykreg-fee-card">
                  <h3 className="ykreg-fee-title">Fee Summary</h3>
                  {savedVisitDates.length > 0 && (
                    <div className="mb-3 p-2.5 bg-indigo-50 rounded-xl border border-indigo-100">
                      <p className="text-xs font-semibold text-indigo-700 mb-1">Visit Dates</p>
                      {savedVisitDates.map((r, i) => (
                        <p key={i} className="text-xs text-indigo-600">
                          {fmtDate(r.start_Date)} – {fmtDate(r.end_Date)}
                        </p>
                      ))}
                    </div>
                  )}
                  {(() => {
                    const totalNights = savedVisitDates.reduce((sum, r) => sum + calculateNights(r.start_Date, r.end_Date), 0);
                    const accomWithNights = savedVisitDates.some((r) => r.is_Accommodation_Needed === 'Y');
                    return (
                      <div className="ykreg-fee-rows">
                        <div className="ykreg-fee-row">
                          <span>Registration Fee</span>
                          <span>${serverRegFee.toFixed(2)}</span>
                        </div>
                        <div className="ykreg-fee-row">
                          <span>Accommodation</span>
                          <span>${effectiveAccomAmt.toFixed(2)}</span>
                        </div>
                        {accomWithNights && pricePerNightGlobal > 0 && totalNights > 0 && (
                          <div className="ykreg-fee-subrow">
                            <span>{totalNights} night{totalNights !== 1 ? 's' : ''} × ${pricePerNightGlobal}/night · 🍽 Food included</span>
                          </div>
                        )}
                        {!accomWithNights && savedVisitDates.length > 0 && (
                          <div className="ykreg-fee-subrow">
                            <span>No accommodation</span>
                          </div>
                        )}
                        <div className="ykreg-fee-row">
                          <span>Transport</span>
                          <span>${transportTotal.toFixed(2)}</span>
                        </div>
                        {hasTransport && (
                          <div className="ykreg-fee-subrow">
                            <span>✈ One-time (Airport → YK Visit)</span>
                          </div>
                        )}
                        <div className="ykreg-fee-total-row">
                          <span>Total</span>
                          <span>${(serverRegFee + effectiveAccomAmt + transportTotal).toFixed(2)}</span>
                        </div>
                        <div className="ykreg-fee-row">
                          <span>Amount Paid</span>
                          <span>${serverAmountPaid.toFixed(2)}</span>
                        </div>
                        <div className="ykreg-fee-balance-row">
                          <span>Balance Due</span>
                          <span>${netToPay ?? 0}</span>
                        </div>
                      </div>
                    );
                  })()}

                  {isFree && (
                    <div>
                      <div className="ykreg-free-banner">🎉 This visit is free! No payment required.</div>
                      <button onClick={handleFreeConfirm} disabled={processing}
                        className="ykreg-free-confirm-btn">
                        {processing ? 'Confirming...' : 'Confirm Registration (Free)'}
                      </button>
                    </div>
                  )}

                  {!isFree && netToPay !== null && netToPay > 0 && (
                    <div>
                      <h4 className="ykreg-payment-title">Payment</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Total due: <strong className="text-gray-800">${netToPay.toFixed ? netToPay.toFixed(2) : netToPay}</strong>
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          const st = lastRegInfo?.studentId
                            ? (unregistered.find((s) => (s.student_Id || s.studentId) === lastRegInfo.studentId) ||
                               registered.find((s) => (s.student_Id || s.studentId) === lastRegInfo.studentId))
                            : null;
                          dispatch(setPaymentInfo({
                            campId,
                            campName: selectedCamp?.Camp_Name || selectedCamp?.camp_Name || `Camp #${campId}`,
                            userId,
                            userFirstName: user?.User_First_Name || user?.user_First_Name || '',
                            userLastName: user?.User_Last_Name || user?.user_Last_Name || '',
                            userEmail: user?.User_Email || user?.user_Email || '',
                            userPhone: user?.User_Phone || user?.user_Phone || '',
                            streetAddress1: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
                            streetAddress2: user?.User_Address_Line2 || user?.user_Address_Line2 || '',
                            city: user?.User_City || user?.user_City || '',
                            zipCode: user?.User_Zipcode || user?.user_Zipcode || '',
                            state: 'GA',
                            country: 'US',
                            entity: 'RMS',
                            studentId: lastRegInfo?.studentId,
                            studentName: st ? `${st.student_First_Name || st.firstName || ''} ${st.student_Last_Name || st.lastName || ''}`.trim() : '',
                            studentGender: st?.student_Gender || st?.gender || '',
                            registrationId: lastRegInfo?.registrationId,
                            totalAmount: netToPay,
                            regFee: serverRegFee,
                            accommodationAmt: effectiveAccomAmt,
                            nights: savedVisitDates.reduce((s, r) => s + calculateNights(r.start_Date, r.end_Date), 0),
                            pricePerNight: pricePerNightGlobal,
                            visitDates: savedVisitDates,
                            centerId,
                          }));
                          navigate('/yk-visit/payment');
                        }}
                        className="ykreg-pay-btn w-full">
                        Proceed to Payment →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!calcLoading && !calculation && (
                <div className="ykreg-calc-placeholder">
                  <div className="ykreg-calc-placeholder-icon">💳</div>
                  <p>Select a visitor and add visit dates to see fee summary</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Mobile Fee Summary FAB + Modal ── */}
        {(calcLoading || calculation) && createPortal(
          <>
            {!showMobileSummary && (
              <button
                onClick={() => setShowMobileSummary(true)}
                className="lg:hidden fixed bottom-5 left-1/2 flex items-center gap-2 text-white font-semibold text-sm py-3 px-6 rounded-full shadow-2xl"
                style={{ zIndex: 9998, transform: 'translateX(-50%)', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}
              >
                💳 Fee Summary {netToPay !== null ? `· $${netToPay} due` : ''}
              </button>
            )}
            {showMobileSummary && (
              <div className="lg:hidden fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4"
                style={{ zIndex: 9999 }}
                onClick={(e) => { if (e.target === e.currentTarget) setShowMobileSummary(false); }}>
                <div className="bg-white rounded-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto shadow-2xl">
                  <div className="flex items-center justify-between p-4 border-b border-gray-100 sticky top-0 bg-white">
                    <h3 className="font-bold text-gray-800">Fee Summary</h3>
                    <button onClick={() => setShowMobileSummary(false)}
                      className="text-gray-400 hover:text-gray-600 text-xl font-bold bg-transparent border-none cursor-pointer">✕</button>
                  </div>
                  <div className="p-4">
                    {calcLoading && (
                      <div className="text-center py-6">
                        <div className="ykreg-spinner-sm mx-auto"></div>
                        <p className="text-sm text-gray-500 mt-2">Calculating fees...</p>
                      </div>
                    )}
                    {!calcLoading && calculation && (
                      <>
                        {savedVisitDates.length > 0 && (
                          <div className="mb-3 p-2.5 bg-indigo-50 rounded-xl border border-indigo-100">
                            <p className="text-xs font-semibold text-indigo-700 mb-1">Visit Dates</p>
                            {savedVisitDates.map((r, i) => (
                              <p key={i} className="text-xs text-indigo-600">{fmtDate(r.start_Date)} – {fmtDate(r.end_Date)}</p>
                            ))}
                          </div>
                        )}
                        {(() => {
                          const tn = savedVisitDates.reduce((s, r) => s + calculateNights(r.start_Date, r.end_Date), 0);
                          const hasAccom = savedVisitDates.some((r) => r.is_Accommodation_Needed === 'Y');
                          return (
                            <div className="space-y-2 text-sm mb-4">
                              <div className="flex justify-between text-gray-600"><span>Registration Fee</span><span>${serverRegFee.toFixed(2)}</span></div>
                              <div className="flex justify-between text-gray-600"><span>Accommodation</span><span>${effectiveAccomAmt.toFixed(2)}</span></div>
                              {hasAccom && pricePerNightGlobal > 0 && tn > 0 && (
                                <div className="text-xs text-gray-400 pl-2">{tn} night{tn !== 1 ? 's' : ''} × ${pricePerNightGlobal}/night · 🍽 Food included</div>
                              )}
                              <div className="flex justify-between text-gray-600"><span>Transport</span><span>${transportTotal.toFixed(2)}</span></div>
                              {hasTransport && (
                                <div className="text-xs text-gray-400 pl-2">✈ One-time (Airport → YK Visit)</div>
                              )}
                              <div className="flex justify-between font-semibold text-gray-800 border-t pt-2"><span>Total</span><span>${(serverRegFee + effectiveAccomAmt + transportTotal).toFixed(2)}</span></div>
                              <div className="flex justify-between text-gray-600"><span>Amount Paid</span><span>${serverAmountPaid.toFixed(2)}</span></div>
                              <div className="flex justify-between font-bold text-lg border-t pt-2 text-teal-700"><span>Balance Due</span><span>${netToPay ?? 0}</span></div>
                            </div>
                          );
                        })()}
                        {isFree && (
                          <div>
                            <div className="ykreg-free-banner">🎉 This visit is free! No payment required.</div>
                            <button onClick={() => { setShowMobileSummary(false); handleFreeConfirm(); }} disabled={processing}
                              className="ykreg-free-confirm-btn">{processing ? 'Confirming...' : 'Confirm Free Visit'}</button>
                          </div>
                        )}
                        {!isFree && netToPay !== null && netToPay > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowMobileSummary(false);
                              const st = lastRegInfo?.studentId
                                ? (unregistered.find((s) => (s.student_Id || s.studentId) === lastRegInfo.studentId) ||
                                   registered.find((s) => (s.student_Id || s.studentId) === lastRegInfo.studentId))
                                : null;
                              dispatch(setPaymentInfo({
                                campId,
                                campName: selectedCamp?.Camp_Name || selectedCamp?.camp_Name || `Camp #${campId}`,
                                userId,
                                userFirstName: user?.User_First_Name || user?.user_First_Name || '',
                                userLastName: user?.User_Last_Name || user?.user_Last_Name || '',
                                userEmail: user?.User_Email || user?.user_Email || '',
                                userPhone: user?.User_Phone || user?.user_Phone || '',
                                streetAddress1: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
                                streetAddress2: user?.User_Address_Line2 || user?.user_Address_Line2 || '',
                                city: user?.User_City || user?.user_City || '',
                                zipCode: user?.User_Zipcode || user?.user_Zipcode || '',
                                state: 'GA',
                                country: 'US',
                                entity: 'RMS',
                                studentId: lastRegInfo?.studentId,
                                studentName: st ? `${st.student_First_Name || st.firstName || ''} ${st.student_Last_Name || st.lastName || ''}`.trim() : '',
                                studentGender: st?.student_Gender || st?.gender || '',
                                registrationId: lastRegInfo?.registrationId,
                                totalAmount: netToPay,
                                regFee: serverRegFee,
                                accommodationAmt: effectiveAccomAmt,
                                nights: savedVisitDates.reduce((s, r) => s + calculateNights(r.start_Date, r.end_Date), 0),
                                pricePerNight: pricePerNightGlobal,
                                visitDates: savedVisitDates,
                                centerId,
                              }));
                              navigate('/yk-visit/payment');
                            }}
                            className="ykreg-pay-btn w-full">
                            Pay ${netToPay} →
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>,
          document.body
        )}

        {/* ── Visit Records popup ── */}
        {expandedStudentId && createPortal((() => {
          const st = registered.find((s) => (s.student_Id || s.studentId) === expandedStudentId);
          if (!st) return null;
          const fn = st.student_First_Name || st.firstName || '';
          const ln = st.student_Last_Name || st.lastName || '';
          const gdr = (st.student_Gender || st.gender || '').toLowerCase();
          const records = visitRecords[expandedStudentId] || [];
          const recLoading = loadingRecords[expandedStudentId];
          return (
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4"
              style={{ zIndex: 9999 }}
              onClick={(e) => { if (e.target === e.currentTarget) { setExpandedStudentId(null); setEditingRecord(null); } }}>
              <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
                <div className="flex items-center gap-3 p-4 border-b border-gray-100 flex-shrink-0">
                  <img src={getAvatarImage(gdr)} alt={fn} className="w-9 h-9 rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-gray-800 text-sm">{fn} {ln}</p>
                    <p className="text-xs text-green-600 font-medium">Visit Records</p>
                  </div>
                  <button
                    onClick={() => { setAddMoreStudentId(expandedStudentId); setAddMoreRows([{ ...DEFAULT_VISIT_DATE_ROW }]); setAddMoreError(null); }}
                    className="text-xs font-semibold py-1 px-3 rounded-full text-white border-none cursor-pointer mr-2"
                    style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}
                  >+ Add Dates</button>
                  <button onClick={() => { setExpandedStudentId(null); setEditingRecord(null); }}
                    className="text-gray-400 hover:text-gray-600 text-xl font-bold bg-transparent border-none cursor-pointer">✕</button>
                </div>
                <div className="overflow-y-auto flex-1 p-4">
                  {recLoading && <p className="text-xs text-gray-500 text-center py-6">Loading records...</p>}
                  {!recLoading && records.length === 0 && (
                    <p className="text-xs text-gray-500 text-center py-6">No visit date records found.</p>
                  )}
                  {!recLoading && records.map((rec) => {
                    const recId = rec.retreat_Registration_Id || rec.id;
                    const detailId = rec.retreat_Registration_Details_Id || rec.id;
                    const isPast = rec.end_Date && new Date(rec.end_Date) < new Date(new Date().toDateString());
                    const isEditing =
                      editingRecord?.record?.retreat_Registration_Details_Id === detailId ||
                      editingRecord?.record?.id === detailId;
                    return (
                      <div key={detailId} className="mb-3 rounded-xl border border-gray-200 overflow-hidden">
                        {isEditing ? (
                          <div className="p-3 bg-indigo-50">
                            <p className="text-xs font-bold text-indigo-700 mb-2">Edit Date Record</p>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Start Date</label>
                                <input type="date" value={editForm.start_Date}
                                  min={todayStr}
                                  onChange={(e) => { const v = e.target.value; setEditForm((p) => ({ ...p, start_Date: v && v >= todayStr ? v : todayStr })); }}
                                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">End Date</label>
                                <input type="date" value={editForm.end_Date} min={editForm.start_Date}
                                  onChange={(e) => setEditForm((p) => ({ ...p, end_Date: e.target.value }))}
                                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                              </div>
                            </div>
                            <div className="flex gap-4 mb-2">
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={editForm.is_Accommodation_Needed === 'Y'}
                                  onChange={(e) => setEditForm((p) => ({ ...p, is_Accommodation_Needed: e.target.checked ? 'Y' : 'N' }))}
                                  className="rounded" />
                                <span className="text-xs text-gray-700">Accommodation</span>
                              </label>
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={editForm.is_Transport === 'Y'}
                                  onChange={(e) => setEditForm((p) => ({ ...p, is_Transport: e.target.checked ? 'Y' : 'N' }))}
                                  className="rounded" />
                                <span className="text-xs text-gray-700">Transport</span>
                              </label>
                            </div>
                            {editForm.start_Date && editForm.end_Date && (() => {
                              const previewNights = calculateNights(editForm.start_Date, editForm.end_Date);
                              const previewFee = calculateAccommodationAmount(editForm.is_Accommodation_Needed, previewNights, parseFloat(accommodationConfig?.accommodationFee || 0));
                              return (
                                <div className="bg-white rounded-lg px-3 py-2 mb-2 border border-indigo-100 flex items-center justify-between">
                                  <span className="text-xs text-gray-500">{previewNights} night{previewNights !== 1 ? 's' : ''}</span>
                                  <span className="text-xs font-bold text-green-700">{previewFee > 0 ? `Accommodation: $${previewFee}` : 'No accommodation charge'}</span>
                                </div>
                              );
                            })()}
                            <div className="flex gap-2">
                              <button onClick={handleSaveEdit} disabled={savingEdit}
                                className="flex-1 py-2 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 border-none cursor-pointer">
                                {savingEdit ? 'Saving...' : 'Save & Pay →'}
                              </button>
                              <button onClick={() => setEditingRecord(null)}
                                className="flex-1 py-2 rounded-lg text-xs font-bold text-gray-600 bg-gray-100 border border-gray-300 cursor-pointer">
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-3 bg-white hover:bg-gray-50">
                            <div>
                              <p className="text-xs font-bold text-gray-800">
                                {fmtDate(rec.start_Date)} → {fmtDate(rec.end_Date)}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Accom: <strong>{rec.is_Accommodation_Needed || 'N'}</strong> &middot; Transport: <strong>{rec.is_Transport || 'N'}</strong>
                                {rec.registration_Fee > 0 && <span> &middot; ${rec.registration_Fee}</span>}
                              </p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              {isPast && <span className="text-xs text-gray-400 italic mr-1">Past</span>}
                              <button onClick={() => !isPast && handleStartEdit(expandedStudentId, rec)}
                                disabled={isPast}
                                title={isPast ? 'Cannot edit past visit dates' : 'Edit'}
                                className={`text-xs font-semibold py-1 px-2.5 rounded-lg border ${isPast ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 cursor-pointer'}`}>Edit</button>
                              <button onClick={() => !isPast && handleDeleteConfirm(expandedStudentId, detailId)}
                                disabled={isPast || deletingId === detailId}
                                title={isPast ? 'Cannot delete past visit dates' : 'Delete'}
                                className={`text-xs font-semibold py-1 px-2.5 rounded-lg border ${isPast ? 'bg-gray-50 text-gray-200 border-gray-100 cursor-not-allowed' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100 cursor-pointer'}`}>
                                {deletingId === detailId ? '...' : 'Del'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })(), document.body)}

        {/* ── Add More Visit Dates popup ── */}
        {addMoreStudentId && createPortal((() => {
          const st = registered.find((s) => (s.student_Id || s.studentId) === addMoreStudentId);
          if (!st) return null;
          const fn = st.student_First_Name || st.firstName || '';
          const ln = st.student_Last_Name || st.lastName || '';
          const gdr = (st.student_Gender || st.gender || '').toLowerCase();
          return (
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4"
              style={{ zIndex: 9999 }}
              onClick={(e) => { if (e.target === e.currentTarget) setAddMoreStudentId(null); }}>
              <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl">
                <div className="flex items-center gap-3 p-4 border-b border-gray-100 sticky top-0 bg-white" style={{ zIndex: 1 }}>
                  <img src={getAvatarImage(gdr)} alt={fn} className="w-9 h-9 rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-gray-800 text-sm">{fn} {ln}</p>
                    <p className="text-xs text-green-600 font-medium">Add More Visit Dates</p>
                  </div>
                  <button onClick={() => setAddMoreStudentId(null)}
                    className="text-gray-400 hover:text-gray-600 text-xl font-bold bg-transparent border-none cursor-pointer">✕</button>
                </div>
                <div className="p-4">
                  <VisitDateForm
                    rows={addMoreRows}
                    onRowChange={(idx, field, value) => setAddMoreRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))}
                    onAddRow={() => setAddMoreRows((prev) => [...prev, { ...DEFAULT_VISIT_DATE_ROW }])}
                    onRemoveRow={(idx) => setAddMoreRows((prev) => prev.filter((_, i) => i !== idx))}
                    onSave={() => handleAddMoreVisitDates(addMoreStudentId)}
                    saving={savingMore}
                    saveError={addMoreError}
                    accommodationConfig={accommodationConfig}
                  />
                </div>
              </div>
            </div>
          );
        })(), document.body)}

        {/* ── Add Visit Dates — popup (all screen sizes) ── */}
        {openStudentId && createPortal((() => {
          const st = unregistered.find((s) => (s.student_Id || s.studentId) === openStudentId);
          if (!st) return null;
          const fn = st.student_First_Name || st.firstName || '';
          const ln = st.student_Last_Name || st.lastName || '';
          const gdr = (st.student_Gender || st.gender || '').toLowerCase();
          return (
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4"
              style={{ zIndex: 9999 }}
              onClick={(e) => { if (e.target === e.currentTarget) setOpenStudentId(null); }}>
              <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl">
                <div className="flex items-center gap-3 p-4 border-b border-gray-100 sticky top-0 bg-white" style={{ zIndex: 1 }}>
                  <img src={getAvatarImage(gdr)} alt={fn} className="w-9 h-9 rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-gray-800 text-sm">{fn} {ln}</p>
                    <p className="text-xs text-indigo-600 font-medium">Add Visit Dates</p>
                  </div>
                  <button onClick={() => setOpenStudentId(null)}
                    className="text-gray-400 hover:text-gray-600 text-xl font-bold bg-transparent border-none cursor-pointer">✕</button>
                </div>
                <div className="p-4">
                  <VisitDateForm
                    rows={visitRows}
                    onRowChange={handleRowChange}
                    onAddRow={handleAddRow}
                    onRemoveRow={handleRemoveRow}
                    onSave={() => handleSaveVisitDates(openStudentId)}
                    saving={savingVisit}
                    saveError={visitSaveError}
                    accommodationConfig={accommodationConfig}
                  />
                </div>
              </div>
            </div>
          );
        })(), document.body)}

        {/* ── Add / Edit Visitor Modal ── */}
        {showVisitorModal && createPortal((
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-b from-white to-gray-50 px-8 py-6 relative"
                style={{ boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1),inset 0 -1px 0 rgba(0,0,0,0.05)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl blur-xl opacity-60 animate-pulse"></div>
                      <div className="relative bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl p-3"
                        style={{ boxShadow: '0 8px 16px -4px rgba(59,130,246,0.6),0 4px 8px -2px rgba(147,51,234,0.6),inset 0 2px 4px rgba(255,255,255,0.3)' }}>
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                        {visitorModalMode === 'edit' ? 'Edit Visitor' : 'Add New Visitor'}
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5 font-medium">
                        {visitorModalMode === 'edit' ? 'Update visitor information' : 'Enter visitor details below'}
                      </p>
                    </div>
                  </div>
                  <button onClick={handleCloseVisitorModal}
                    className="group relative text-gray-400 hover:text-gray-700 rounded-xl p-2.5 transition-all duration-300 transform hover:rotate-90 hover:scale-110">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Form */}
              <div className="p-6 space-y-5">
                {/* First Name */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" name="firstName" value={visitorFormData.firstName} placeholder="Enter first name" maxLength="100"
                    onChange={e => setVisitorFormData(p => ({ ...p, firstName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${visitorFormErrors.firstName ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200 hover:border-blue-300'}`} />
                  {visitorFormErrors.firstName && <p className="text-red-500 text-xs mt-1">First name is required</p>}
                </div>

                {/* Last Name */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" name="lastName" value={visitorFormData.lastName} placeholder="Enter last name" maxLength="100"
                    onChange={e => setVisitorFormData(p => ({ ...p, lastName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${visitorFormErrors.lastName ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-purple-500 focus:ring-purple-200 hover:border-purple-300'}`} />
                  {visitorFormErrors.lastName && <p className="text-red-500 text-xs mt-1">Last name is required</p>}
                </div>

                {/* Date of Birth */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input type="date" name="dateOfBirth" value={visitorFormData.dateOfBirth}
                    max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 3); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })()}
                    min={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 120); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })()}
                    onChange={e => setVisitorFormData(p => ({ ...p, dateOfBirth: e.target.value }))}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${visitorFormErrors.dateOfBirth ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-pink-500 focus:ring-pink-200 hover:border-pink-300'}`} />
                  {visitorFormErrors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{typeof visitorFormErrors.dateOfBirth === 'string' ? visitorFormErrors.dateOfBirth : 'Date of birth is required'}</p>}
                </div>

                {/* Gender */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select name="gender" value={visitorFormData.gender}
                    onChange={e => setVisitorFormData(p => ({ ...p, gender: e.target.value }))}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 appearance-none bg-white ${visitorFormErrors.gender ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200 hover:border-indigo-300'}`}>
                    <option value="">Select gender</option>
                    <option value="Male">👦 Male</option>
                    <option value="Female">👧 Female</option>
                  </select>
                  {visitorFormErrors.gender && <p className="text-red-500 text-xs mt-1">Gender is required</p>}
                </div>

                {/* Relationship */}
                <div className="transform transition-all duration-200 hover:scale-[1.01]">
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Relationship <span className="text-red-500">*</span>
                  </label>
                  <select name="relationship" value={visitorFormData.relationship}
                    onChange={e => setVisitorFormData(p => ({ ...p, relationship: e.target.value }))}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 appearance-none bg-white ${visitorFormErrors.relationship ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-200 focus:border-green-500 focus:ring-green-200 hover:border-green-300'}`}>
                    <option value="">Select relationship</option>
                    <option value="Self">🙋 Self</option>
                    <option value="Spouse">💑 Spouse</option>
                    <option value="Son">👦 Son</option>
                    <option value="Daughter">👧 Daughter</option>
                    <option value="Other">👤 Other</option>
                  </select>
                  {visitorFormErrors.relationship && <p className="text-red-500 text-xs mt-1">Relationship is required</p>}
                </div>

                {visitorSaveError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                    <p className="text-red-600 text-sm text-center">{visitorSaveError}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-6 pb-6 pt-2 flex gap-3">
                <button onClick={handleCloseVisitorModal}
                  className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
                <button onClick={handleSaveVisitor} disabled={savingVisitor}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] flex items-center justify-center gap-2"
                  style={{ opacity: savingVisitor ? 0.7 : 1, cursor: savingVisitor ? 'not-allowed' : 'pointer' }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {savingVisitor ? 'Saving...' : visitorModalMode === 'edit' ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ), document.body)}

        {showSuccessModal && createPortal((
          <div className="ykreg-modal-overlay">
            <div className="ykreg-modal">
              <div className="ykreg-modal-icon-success">
                <svg className="ykreg-modal-icon-svg-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="ykreg-modal-title-success">Registration Confirmed!</h2>
              <p className="ykreg-modal-body">
                {paymentResult?.amount > 0
                  ? `Payment of $${paymentResult.amount} processed successfully.`
                  : 'Your free visit registration is confirmed.'}
              </p>
              {paymentResult?.transId && paymentResult.transId !== 'FREE' && (
                <p className="ykreg-modal-trans-id">Transaction ID: {paymentResult.transId}</p>
              )}
              <p className="ykreg-modal-auto-close">This window will close automatically in 20 seconds.</p>
              <button onClick={() => { setShowSuccessModal(false); }}
                className="ykreg-modal-btn-success">Done</button>
            </div>
          </div>
        ), document.body)}

        {showErrorModal && (
          <div className="ykreg-modal-overlay">
            <div className="ykreg-modal">
              <div className="ykreg-modal-icon-error">
                <svg className="ykreg-modal-icon-svg-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="ykreg-modal-title-error">Payment Failed</h2>
              <p className="ykreg-modal-body">{errorMessage}</p>
              <button onClick={() => setShowErrorModal(false)}
                className="ykreg-modal-btn-error">Try Again</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default YkVisitRegistration;
